import { useState } from "react";
import Input from "components/ui/Input";
import Button from "components/ui/Button";
import { toast } from "react-toastify";
import { updateUserProfilePassword } from "services/accountService";
import { set } from "date-fns";

const PasswordChangeSection = () => {
    const [formData, setFormData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [showPasswords, setShowPasswords] = useState({
        currentPassword: false,
        newPassword: false,
        confirmNewPassword: false,
    });

    const passwordStrength = (password) => {
        if (!password) return { strength: 0, label: "", color: "" };
        
        let strength = 0;
        if (password.length >= 8) strength++;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
        if (/\d/.test(password)) strength++;
        if (/[^a-zA-Z\d]/.test(password)) strength++;

        const strengthLevels = [
            { strength: 0, label: "", color: "" },
            { strength: 1, label: "Weak", color: "text-destructive" },
            { strength: 2, label: "Fair", color: "text-warning" },
            { strength: 3, label: "Good", color: "text-blue-500" },
            { strength: 4, label: "Strong", color: "text-success" },
        ];

        return strengthLevels[strength];
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.currentPassword) {
            newErrors.currentPassword = "Current password is required";
        }
        if (!formData.newPassword) {
            newErrors.newPassword = "New password is required";
        } else if (formData.newPassword.length < 8) {
            newErrors.newPassword = "Password must be at least 8 characters long";
        }
        if (!formData.confirmNewPassword) {
            newErrors.confirmNewPassword = "Please confirm your password";
        } else if (formData.newPassword !== formData.confirmNewPassword) {
            newErrors.confirmNewPassword = "Passwords do not match";
        }
        if (formData.currentPassword === formData.newPassword) {
            newErrors.newPassword = "New password must be different from current password";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccessMessage("");
        setErrorMessage("");

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const response = await updateUserProfilePassword(formData);
            if (response && response.success) {
                setFormData({
                    currentPassword: "",
                    newPassword: "",
                    confirmNewPassword: "",
                });
                setShowPasswords({
                    currentPassword: false,
                    newPassword: false,
                    confirmNewPassword: false,
                });
                toast.success(response?.message || "Password changed successfully!");
                setSuccessMessage("Password changed successfully!");
            }
            else{
                toast.error(response?.message || "Failed to change password");
                if (response?.errors && Array.isArray(response.errors)) {
                    let errorSummary = '';
                    response.errors.forEach(error => {
                        errorSummary += `- ${error}\n`;
                    });
                    setErrorMessage(errorSummary);
                }
            }            
        } catch (error) {
            setErrorMessage(error.message || "Failed to change password");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFormData({
            currentPassword: "",
            newPassword: "",
            confirmNewPassword: "",
        });
        setErrors({});
        setShowPasswords({
            currentPassword: false,
            newPassword: false,
            confirmNewPassword: false,
        });
    };

    const newPasswordStrength = passwordStrength(formData.newPassword);

    return (
        <div className="bg-card border border-border rounded-lg p-6">
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-foreground">Change Password</h2>
                <p className="text-sm text-muted-foreground mt-1">
                    Update your password to keep your account secure
                </p>
            </div>

            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
                <h3 className="font-semibold text-sm text-blue-900 dark:text-blue-100 mb-2">
                    Password Requirements:
                </h3>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                    <li>✓ At least 8 characters long</li>
                    <li>✓ Combination of uppercase and lowercase letters</li>
                    <li>✓ At least one number</li>
                    <li>✓ At least one special character (!@#$%^&*)</li>
                </ul>
            </div>

            {successMessage && (
                <div className="mb-4 p-4 bg-success/10 border border-success text-success rounded-md">
                    {successMessage}
                </div>
            )}

            {errorMessage && (
                <div className="mb-4 p-4 bg-destructive/10 border border-destructive text-destructive rounded-md whitespace-pre-line">
                    {errorMessage}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <div className="relative">
                        <Input
                            label="Current Password"
                            type={showPasswords.currentPassword ? "text" : "password"}
                            name="currentPassword"
                            value={formData.currentPassword}
                            onChange={handleChange}
                            error={errors.currentPassword}
                            required
                            placeholder="Enter your current password"
                        />
                        <button
                            type="button"
                            onClick={() => togglePasswordVisibility("currentPassword")}
                            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground text-lg"
                        >
                            {showPasswords.currentPassword ? "👁️" : "👁️‍🗨️"}
                        </button>
                    </div>
                    {errors.currentPassword && (
                        <p className="mt-1 text-sm text-destructive">{errors.currentPassword}</p>
                    )}
                </div>

                <div>
                    <div className="relative">
                        <Input
                            label="New Password"
                            type={showPasswords.newPassword ? "text" : "password"}
                            name="newPassword"
                            value={formData.newPassword}
                            onChange={handleChange}
                            error={errors.newPassword}
                            required
                            placeholder="Enter your new password"
                        />
                        <button
                            type="button"
                            onClick={() => togglePasswordVisibility("newPassword")}
                            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground text-lg"
                        >
                            {showPasswords.newPassword ? "👁️" : "👁️‍🗨️"}
                        </button>
                    </div>
                    {errors.newPassword && (
                        <p className="mt-1 text-sm text-destructive">{errors.newPassword}</p>
                    )}
                    {formData.newPassword && newPasswordStrength.label && (
                        <p className={`mt-2 text-sm font-medium ${newPasswordStrength.color}`}>
                            Password Strength: {newPasswordStrength.label}
                        </p>
                    )}
                </div>

                <div>
                    <div className="relative">
                        <Input
                            label="Confirm Password"
                            type={showPasswords.confirmNewPassword ? "text" : "password"}
                            name="confirmNewPassword"
                            value={formData.confirmNewPassword}
                            onChange={handleChange}
                            error={errors.confirmNewPassword}
                            required
                            placeholder="Confirm your new password"
                        />
                        <button
                            type="button"
                            onClick={() => togglePasswordVisibility("confirmNewPassword")}
                            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground text-lg"
                        >
                            {showPasswords.confirmNewPassword ? "👁️" : "👁️‍🗨️"}
                        </button>
                    </div>
                    {errors.confirmNewPassword && (
                        <p className="mt-1 text-sm text-destructive">{errors.confirmNewPassword}</p>
                    )}
                </div>

                <div className="flex gap-3 pt-4">
                    <Button
                        type="submit"
                        disabled={loading}
                        className="px-6"
                    >
                        {loading ? "Updating..." : "Update Password"}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleReset}
                        disabled={loading}
                    >
                        Reset
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default PasswordChangeSection;
